import React from 'react';
import {
  signIn as amplifySignIn,
  signOut as amplifySignOut,
  confirmSignIn as amplifyConfirmSignIn,
  updatePassword,
  confirmResetPassword,
  resetPassword as amplifyForgotPassword,
  fetchAuthSession,
  updateMFAPreference,
  fetchUserAttributes,
} from 'aws-amplify/auth';
import {
  CognitoUser,
  CognitoAccessToken,
  CognitoIdToken,
  CognitoRefreshToken,
  CognitoUserPool,
  CognitoUserSession,
} from 'amazon-cognito-identity-js';
import dayjs from 'dayjs';
import { USER_INFO_STORAGE_KEY, PANTHER_CONFIG } from 'Source/constants';
import storage from 'Helpers/storage';
import { Permission } from 'Generated/schema';
import urls from 'Source/urls';
import useEffectOnUpdates from 'Hooks/useEffectOnUpdates';

// Challenge names come from Amplify React docs: https://docs.amplify.aws/react/build-a-backend/auth/connect-your-frontend/sign-in/
export enum CHALLENGE_NAMES {
  CONTINUE_SIGN_IN_WITH_TOTP_SETUP = 'CONTINUE_SIGN_IN_WITH_TOTP_SETUP',
  CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED = 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED',
  CONFIRM_SIGN_IN_WITH_TOTP_CODE = 'CONFIRM_SIGN_IN_WITH_TOTP_CODE',
}

interface AuthError {
  /** unique error code */
  code: string;

  /** verbose exception that happened */
  message: string;

  /** optional | name of the exception, usually just the code itself */
  name?: string;
}

interface UserInfoIdentities {
  userId?: string;
  providerName?: string;
  providerType?: string;
}

/* eslint-disable  camelcase  */
export interface EnhancedCognitoUser {
  username: string;
  userId: string;
  attributes: {
    email: string;
    email_verified: boolean;
    family_name?: string;
    given_name?: string;
    sub: string;
    identities?: string;
  };
  signInUserSession?: {
    accessToken?: {
      payload: {
        'cognito:groups'?: string[];
        auth_time: number;
      };
    };
    idToken?: {
      payload: {
        pylon_email_hash?: string;
      };
    };
  };
  nextStep?: {
    signInStep?: CHALLENGE_NAMES;
    totpSetupDetails?: {
      sharedSecret?: string;
    };
  };
}
/* eslint-enable  camelcase  */

interface SingleSignOnSettings {
  enabled: boolean;
  enforced: boolean;
}

export type UserInfo = {
  id: string;
  email: string;
  emailVerified: boolean;
  enabled: boolean;
  givenName?: string;
  familyName?: string;
  permissions: Permission[];
  identities?: [UserInfoIdentities];
  // Unix timestamp representing the time when the original authentication occurred
  authenticationTime: number;
  pylonEmailHash?: string;
  hasSamlDefaultRole?: boolean;
};

interface SignOutParams {
  global?: boolean;
  onSuccess?: () => void;
  onError?: (err: AuthError) => void;
}

interface SignInParams {
  email: string;
  password: string;
  onSuccess?: () => void;
  onError?: (err: AuthError) => void;
}

interface SingleSignOnParams {
  code: string;
  onSuccess?: () => void;
  onError?: (err: AuthError) => void;
}

interface SingleSignOnSettingRetrievalParams {
  onSuccess?: (data: SingleSignOnSettings) => void;
  onError?: (err: AuthError) => void;
}

interface ConfirmSignInParams {
  mfaCode: string;
  onSuccess?: () => void;
  onError?: (err: AuthError) => void;
}

interface VerifyTotpSetupParams {
  mfaCode: string;
  onSuccess?: () => void;
  onError?: (err: AuthError) => void;
}

interface SetNewPasswordParams {
  newPassword: string;
  onSuccess?: () => void;
  onError?: (err: AuthError) => void;
}

interface ChangePasswordParams {
  oldPassword: string;
  newPassword: string;
  onSuccess?: () => void;
  onError?: (err: AuthError) => void;
}

interface ResetPasswordParams {
  token: string;
  email: string;
  newPassword: string;
  onSuccess?: () => void;
  onError?: (err: AuthError) => void;
}

interface ForgotPasswordParams {
  email: string;
  onSuccess?: () => void;
  onError?: (err: AuthError) => void;
}

interface RefetchUserInfoParams {
  onSuccess?: () => void;
  onError?: (err: AuthError) => void;
}

/*
  We intentionally use `undefined` and `null` in the interface below to showcase the possible values
 */
export interface AuthContextValue {
  isAuthenticated: boolean | undefined;
  currentAuthChallengeName: CHALLENGE_NAMES | null;
  userInfo: UserInfo | null;
  signIn: (params: SignInParams) => Promise<void>;
  singleSignOn: (params: SingleSignOnParams) => void;
  confirmSignIn: (params: ConfirmSignInParams) => Promise<void>;
  refetchUserInfo: (params?: RefetchUserInfoParams) => Promise<void>;
  setNewPassword: (params: SetNewPasswordParams) => Promise<void>;
  verifyTotpSetup: (params: VerifyTotpSetupParams) => Promise<void>;
  getTotpSharedSecret: () => string;
  retrieveSsoSettings: (params: SingleSignOnSettingRetrievalParams) => Promise<void>;
  signOut: (params?: SignOutParams) => Promise<void>;
  changePassword: (params: ChangePasswordParams) => Promise<void>;
  resetPassword: (params: ResetPasswordParams) => Promise<void>;
  forgotPassword: (params: ForgotPasswordParams) => Promise<void>;
}

const AuthContext = React.createContext<AuthContextValue>(undefined);

// Helper function to convert v6 tokens to EnhancedCognitoUser format
const convertTokensToEnhancedCognitoUser = (
  tokens: any,
  userAttributes?: any,
): EnhancedCognitoUser => {
  const idTokenPayload = tokens.idToken.payload;
  const accessTokenPayload = tokens.accessToken.payload;

  const attributes = {
    ...userAttributes,
    ...idTokenPayload,
  };

  return {
    username: accessTokenPayload.username,
    userId: attributes.sub,
    attributes: {
      'custom:role_id': attributes['custom:role_id'],
      'custom:role_name': attributes['custom:role_name'],
      email: attributes.email,
      email_verified: attributes.email_verified,
      family_name: attributes.family_name,
      given_name: attributes.given_name,
      sub: attributes.sub,
      identities:
        attributes.identities && typeof attributes.identities === 'string'
          ? attributes.identities
          : JSON.stringify(attributes.identities),
    },
    signInUserSession: {
      accessToken: {
        payload: {
          'cognito:groups': accessTokenPayload['cognito:groups'],
          auth_time: accessTokenPayload.auth_time,
        },
      },
      idToken: {
        payload: {
          pylon_email_hash: attributes.pylon_email_hash,
        },
      },
    },
  } as EnhancedCognitoUser;
};

// We check if there was a previous session for this user already present. We use that to
// decide whether the user should be considered authenticated on mount time.
const checkPreviousUserSessionExistence = () => {
  const userSessionExists = Boolean(
    storage.local.read(
      `CognitoIdentityServiceProvider.${PANTHER_CONFIG.WEB_APPLICATION_USER_POOL_CLIENT_ID}.LastAuthUser`,
    ),
  );
  if (!userSessionExists) {
    return false;
  }
  const userInfo = storage.local.read<UserInfo>(USER_INFO_STORAGE_KEY);
  if (!userInfo?.authenticationTime) {
    return false;
  }
  // Manually check if the refresh token has expired by the time the user revisits.
  return dayjs
    .unix(userInfo.authenticationTime)
    .add(PANTHER_CONFIG.REFRESH_TOKEN_EXPIRATION_HOURS, 'hours')
    .isAfter(dayjs(), 'minute');
};

const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // Stores whether the system should consider the current user as logged-in or not. This can be
  // true without `authUser` being present, since `authUser` comes asynchronously from Cognito, thus
  // it's *always* initially `null`.
  const [isAuthenticated, setAuthenticated] = React.useState(checkPreviousUserSessionExistence());
  // Stores the currently authenticated user of the app
  const [authUser, setAuthUser] = React.useState<EnhancedCognitoUser | null>(null);

  /*
   * Isolate the userInfo from the user. This is an object that will persist in our storage so that
   * we can boot up the user's information (name, token, etc.) the next time he visits the app. The
   * value changes whenever the cognito session changes
   */
  const userInfo = React.useMemo<UserInfo>(() => {
    // if a user is present, derive the user info from him
    // Check if this is calculated
    if (authUser?.attributes) {
      // eslint-disable-next-line  camelcase
      const { family_name, given_name, email_verified, ...rest } = authUser.attributes;
      return {
        ...rest,
        id: authUser.username, // we want the ID to be the username for SSO support
        enabled: true,
        familyName: family_name,
        givenName: given_name,
        emailVerified: email_verified,
        identities: authUser?.attributes?.identities
          ? JSON.parse(authUser.attributes.identities)
          : [],
        permissions: (authUser?.signInUserSession?.accessToken?.payload?.['cognito:groups'] ??
          []) as Permission[],
        authenticationTime: authUser?.signInUserSession?.accessToken?.payload?.auth_time,
        hasSamlDefaultRole:
          Boolean(authUser?.attributes?.['custom:role_id']) ||
          Boolean(authUser?.attributes?.['custom:role_name']),
        pylonEmailHash: authUser?.signInUserSession?.idToken?.payload?.pylon_email_hash,
      };
    }

    // if no user is present, attempt to return data from the stored session. This is true when
    // the request to get the cognito `authUser` is in flight and hasn't returned yet
    if (isAuthenticated) {
      return storage.local.read<UserInfo>(USER_INFO_STORAGE_KEY);
    }

    // if no prev session exists and the user is not logged-in, then there is no userInfo
    return null;
  }, [isAuthenticated, authUser]);

  /**
   * Every time the `userInfo` is updated, we want to store this value in our storage in order to
   * remember it for future logins. If we don't do that, then we don't have a way of knowing the
   * user on mount time.
   */
  React.useEffect(() => {
    if (userInfo) {
      storage.local.write(USER_INFO_STORAGE_KEY, userInfo);
    } else {
      storage.local.delete(USER_INFO_STORAGE_KEY);
    }
  }, [userInfo]);

  /**
   * @public
   * Check if the SSO is enabled for this account
   *
   */
  const retrieveSsoSettings = React.useCallback(
    async ({ onSuccess = () => {}, onError = () => {} }: SingleSignOnSettingRetrievalParams) => {
      try {
        const publicApiServer = `https://${PANTHER_CONFIG.PUBLIC_CORE_API_HOST}/saml`;

        // Default options are marked with *
        const response = await fetch(publicApiServer);

        const data = await response.json();
        if (data.error) {
          onError(data.error as AuthError);
        }

        onSuccess(data);
      } catch (err) {
        onError(err as AuthError);
      }
    },
    [],
  );

  /**
   * @public
   * Signs the user in our system
   *
   */
  const signIn = React.useCallback(
    async ({ email, password, onSuccess = () => {}, onError = () => {} }: SignInParams) => {
      try {
        const { isSignedIn, nextStep } = await amplifySignIn({
          username: email,
          password,
        });

        if (isSignedIn) {
          // Get tokens (contains all user data)
          const { tokens } = await fetchAuthSession();

          setAuthUser(convertTokensToEnhancedCognitoUser(tokens));
          onSuccess();
        } else if (nextStep) {
          // Handle MFA or other challenges
          const challengeUser = {
            username: email,
            attributes: { email },
            nextStep,
          };
          setAuthUser(challengeUser as unknown as EnhancedCognitoUser);
          onSuccess();
        } else {
          throw new Error('An error occurred. Please try again.');
        }
      } catch (err) {
        onError(err as AuthError);
      }
    },
    [],
  );

  /**
   * @public
   * Signs the user in using an external SSO provider
   *
   */
  const singleSignOn = React.useCallback(
    async ({ code, onSuccess = () => {}, onError = () => {} }: SingleSignOnParams) => {
      try {
        const authServer = `https://${PANTHER_CONFIG.WEB_APPLICATION_USER_POOL_HOST}/oauth2/token`;
        const payload =
          `grant_type=authorization_code` +
          `&client_id=${PANTHER_CONFIG.WEB_APPLICATION_USER_POOL_CLIENT_ID}` +
          `&code=${code}` +
          `&redirect_uri=${window.location.origin}${urls.account.auth.singleSignOn()}`;

        // Default options are marked with *
        const response = await fetch(authServer, {
          method: 'POST',
          headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
          cache: 'no-cache',
          credentials: 'omit',
          referrerPolicy: 'no-referrer',
          body: payload,
        });

        const data = await response.json();
        if (data.error) {
          onError(data.error as AuthError);
        }

        // Create a session instance using the tokens
        const session = new CognitoUserSession({
          IdToken: new CognitoIdToken({ IdToken: data.id_token }),
          RefreshToken: new CognitoRefreshToken({ RefreshToken: data.refresh_token }),
          AccessToken: new CognitoAccessToken({ AccessToken: data.access_token }),
        });

        // Create a user pool object
        const userPool = new CognitoUserPool({
          UserPoolId: PANTHER_CONFIG.WEB_APPLICATION_USER_POOL_ID,
          ClientId: PANTHER_CONFIG.WEB_APPLICATION_USER_POOL_CLIENT_ID,
        });

        // Then create a new user object from that pool
        const user = new CognitoUser({
          Username: session.getIdToken().decodePayload()['cognito:username'],
          Pool: userPool,
        });

        // Connect user to the session
        user.setSignInUserSession(session);

        // We need to call fetchUserAttributes because the tokens do not contain all of the user
        // attributes for SSO users.
        const userAttributes = await fetchUserAttributes();

        // With the user connected to the session, fetch the user's details from aws-amplify,
        // then save the user to the state
        const { tokens } = await fetchAuthSession({ forceRefresh: true });
        setAuthUser(convertTokensToEnhancedCognitoUser(tokens, userAttributes));

        onSuccess();
      } catch (err) {
        onError(err as AuthError);
      }
    },
    [],
  );

  /**
   * @public
   * Signs the user out. Can be global sign out (all devices) or just local (this device only)
   *
   */
  const signOut = React.useCallback(
    ({ global = false, onSuccess = () => {}, onError = () => {} }: SignOutParams = {}) => {
      return amplifySignOut({ global })
        .then(async () => {
          onSuccess();
          // Calling the Cognito logout endpoint to properly sign users out in the federated login case.
          // This flow works correctly in both federated and non-federated sign-out cases.
          // See https://github.com/aws-amplify/amplify-js/issues/3933 and https://docs.aws.amazon.com/cognito/latest/developerguide/logout-endpoint.html
          const logoutUrl =
            `https://${PANTHER_CONFIG.WEB_APPLICATION_USER_POOL_HOST}/logout` +
            `?client_id=${PANTHER_CONFIG.WEB_APPLICATION_USER_POOL_CLIENT_ID}` +
            `&logout_uri=${encodeURI(`${window.location.origin}${urls.account.auth.signIn()}`)}`;
          window.location.href = logoutUrl;
        })
        .catch(onError);
    },
    [],
  );

  /**
   *
   * @public
   * Verifies that the user is not an imposter by verifying the TOTP challenge that the user was
   * presented with. This function verifies that the one-time password was indeed correct.
   *
   */
  const confirmSignIn = React.useCallback(
    async ({ mfaCode, onSuccess = () => {}, onError = () => {} }: ConfirmSignInParams) => {
      try {
        const { isSignedIn, nextStep } = await amplifyConfirmSignIn({
          challengeResponse: mfaCode,
        });

        if (isSignedIn) {
          const { tokens } = await fetchAuthSession();
          setAuthUser(convertTokensToEnhancedCognitoUser(tokens));
        } else if (nextStep) {
          // Update authUser with the new nextStep
          const challengeUser = {
            username: authUser?.username || '',
            attributes: authUser?.attributes || {},
            nextStep,
          };
          setAuthUser(challengeUser as unknown as EnhancedCognitoUser);
        }

        onSuccess();
      } catch (err) {
        onError(err as AuthError);
      }
    },
    [authUser],
  );

  /**
   *
   * @public
   * Verifies that the user has correctly setup the TOTP
   *
   */
  const verifyTotpSetup = React.useCallback(
    async ({ mfaCode, onSuccess = () => {}, onError = () => {} }: VerifyTotpSetupParams) => {
      try {
        const { isSignedIn, nextStep } = await amplifyConfirmSignIn({
          challengeResponse: mfaCode,
        });

        if (isSignedIn) {
          // Update MFA preference to TOTP
          await updateMFAPreference({ totp: 'PREFERRED' });

          // Get the tokens and update authUser
          const { tokens } = await fetchAuthSession();
          setAuthUser(convertTokensToEnhancedCognitoUser(tokens));
        } else if (nextStep) {
          // Update authUser with the new nextStep
          const challengeUser = {
            username: authUser?.username || '',
            attributes: authUser?.attributes || {},
            nextStep,
          };
          setAuthUser(challengeUser as unknown as EnhancedCognitoUser);
        }

        onSuccess();
      } catch (err) {
        onError(err as AuthError);
      }
    },
    [authUser],
  );

  /**
   * @public
   * Gets the shared secret for the user's TOTP setup
   */
  const getTotpSharedSecret = React.useCallback(() => {
    return authUser?.nextStep?.totpSetupDetails?.sharedSecret || '';
  }, [authUser?.nextStep?.totpSetupDetails?.sharedSecret]);

  /**
   * @public
   * Sets a new password for the user when he has a temporary one
   *
   */
  const setNewPassword = React.useCallback(
    async ({ newPassword, onSuccess = () => {}, onError = () => {} }: SetNewPasswordParams) => {
      try {
        const { isSignedIn, nextStep } = await amplifyConfirmSignIn({
          challengeResponse: newPassword,
        });

        if (isSignedIn) {
          // Get the tokens and update authUser
          const { tokens } = await fetchAuthSession();
          setAuthUser(convertTokensToEnhancedCognitoUser(tokens));
        } else if (nextStep) {
          // Update authUser with the new nextStep (e.g., CONTINUE_SIGN_IN_WITH_TOTP_SETUP)
          const updatedChallengeUser = {
            username: authUser?.username || '',
            attributes: authUser?.attributes || {},
            nextStep,
          };
          setAuthUser(updatedChallengeUser as unknown as EnhancedCognitoUser);
        }

        onSuccess();
      } catch (err) {
        onError(err as AuthError);
      }
    },
    [authUser],
  );

  /**
   * @public
   * Changes the current password for the user. This is a different workflow than `setPassword`,
   * since the user doesn't have a temporary password here and he also needs to provide his old
   * password
   */
  const changePassword = React.useCallback(
    async ({
      oldPassword,
      newPassword,
      onSuccess = () => {},
      onError = () => {},
    }: ChangePasswordParams) => {
      try {
        await updatePassword({ oldPassword, newPassword });

        onSuccess();
      } catch (err) {
        onError(err as AuthError);
      }
    },
    [],
  );

  /**
   * @public
   * Resets the current password for the user to the value he has given. This is a different
   * workflow than `setPassword` or `changePassword` since the user doesn't have knowledge of his
   * current password, except for a reset link that he received through an email. This link
   * contained the reset token used below
   */
  const resetPassword = React.useCallback(
    async ({
      email,
      token,
      newPassword,
      onSuccess = () => {},
      onError = () => {},
    }: ResetPasswordParams) => {
      try {
        await confirmResetPassword({ username: email, confirmationCode: token, newPassword });

        onSuccess();
      } catch (err) {
        onError(err as AuthError);
      }
    },
    [],
  );

  /**
   * @public
   * A method to initiate a forgot password request. This will send the user an email containing
   * a link to reset his password
   */
  const forgotPassword = React.useCallback(
    async ({ email, onSuccess = () => {}, onError = () => {} }: ForgotPasswordParams) => {
      try {
        await amplifyForgotPassword({ username: email });
        onSuccess();
      } catch (err) {
        onError(err as AuthError);
      }
    },
    [],
  );

  /**
   * @public
   * A method to refetch user info in order to update state when a user edits self
   */
  const refetchUserInfo = React.useCallback(
    async ({ onSuccess = () => {}, onError = () => {} }: RefetchUserInfoParams = {}) => {
      try {
        // Including the forceRefresh option ensures we get the latest user info
        const { tokens } = await fetchAuthSession({ forceRefresh: true });
        setAuthUser(convertTokensToEnhancedCognitoUser(tokens));
        onSuccess();
      } catch (err) {
        onError(err as AuthError);
        signOut();
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  /**
   * During mount time only, after having - possibly - set up the Auth configuration, attempt to
   * boot up the user from a previous session
   */
  React.useEffect(() => {
    if (isAuthenticated) {
      const fetchUser = async () => {
        try {
          const { tokens } = await fetchAuthSession();
          // We need to call fetchUserAttributes because the tokens do not contain all of the user
          // attributes for SSO users.
          const userAttributes = await fetchUserAttributes();
          setAuthUser(convertTokensToEnhancedCognitoUser(tokens, userAttributes));
        } catch {
          signOut();
        }
      };
      fetchUser();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /*
   * Sign the user in when there are no more challenges present. This hook only runs during updates
   * since the hook above handles the mount-time.
   *
   * This is because, during mounts, the `authUser` is `null`, but we want to keep the initial value
   * of `isAuthenticated` since we want to "boot" from localStorage
   */
  useEffectOnUpdates(() => {
    if (!authUser || 'nextStep' in authUser) {
      setAuthenticated(false);
    } else {
      setAuthenticated(true);
    }
  }, [authUser]);

  /**
   * @public
   * The `isAuthenticated` has an undefined value whenever we haven't yet figured out if the user
   * is or isn't authenticated cause we are on the process of examining his token. It has a boolean
   * value in any other case
   */
  const contextValue = React.useMemo(
    () => ({
      isAuthenticated,
      currentAuthChallengeName: authUser?.nextStep?.signInStep || null,
      userInfo,
      refetchUserInfo,
      retrieveSsoSettings,
      signIn,
      confirmSignIn,
      singleSignOn,
      signOut,
      setNewPassword,
      changePassword,
      resetPassword,
      forgotPassword,
      getTotpSharedSecret,
      verifyTotpSetup,
    }),
    // FIXME: look into hook dependencies
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isAuthenticated, authUser],
  );

  return <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>;
};

const MemoizedAuthProvider = React.memo(AuthProvider);

export { AuthContext, MemoizedAuthProvider as AuthProvider };
